import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http, fmtErr } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Gem, MapPin } from "lucide-react";

const TYPES = ["wedding", "engagement", "henna", "birthday", "other"];

export default function EventWizard() {
  const { t } = useI18n();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [f, setF] = useState({
    title: "", event_type: "wedding",
    starts_at: "", ends_at: "",
    location: "", description: "",
    stream_provider: "youtube",
    stream_url: "", password: "",
    currency: "TRY", accept_gold: true, accept_cash: true,
    jeweler_id: "",
  });
  const [saving, setSaving] = useState(false);
  const [jewelers, setJewelers] = useState([]);
  const [cityFilter, setCityFilter] = useState("");
  const total = 4;
  const upd = (k, v) => setF({ ...f, [k]: v });

  useEffect(() => {
    http.get(`/jewelers${cityFilter ? `?city=${encodeURIComponent(cityFilter)}` : ""}`).then(r => setJewelers(r.data)).catch(() => {});
  }, [cityFilter]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...f,
        starts_at: new Date(f.starts_at).toISOString(),
        ends_at: f.ends_at ? new Date(f.ends_at).toISOString() : null,
        jeweler_id: f.jeweler_id || null,
      };
      const { data } = await http.post("/events", payload);
      toast.success("Etkinlik oluşturuldu");
      nav(`/events/${data.id}`);
    } catch (e) { toast.error(fmtErr(e)); }
    setSaving(false);
  };

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-10">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{t.step} {step} {t.of} {total}</div>
        <Progress value={(step / total) * 100} className="h-1" />
      </div>
      <Card className="p-6 sm:p-8">
        {step === 1 && (
          <div className="space-y-5" data-testid="wizard-step-1">
            <h2 className="font-serif text-3xl">Etkinlik Detayları</h2>
            <div><Label>{t.event_title}</Label><Input value={f.title} onChange={(e) => upd("title", e.target.value)} data-testid="ev-title" placeholder="Ayşe & Mehmet Düğünü" /></div>
            <div>
              <Label>{t.event_type}</Label>
              <Select value={f.event_type} onValueChange={(v) => upd("event_type", v)}>
                <SelectTrigger data-testid="ev-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((x) => <SelectItem key={x} value={x}>{t.types[x]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>{t.starts_at}</Label><Input type="datetime-local" value={f.starts_at} onChange={(e) => upd("starts_at", e.target.value)} data-testid="ev-starts" /></div>
              <div><Label>{t.ends_at}</Label><Input type="datetime-local" value={f.ends_at} onChange={(e) => upd("ends_at", e.target.value)} data-testid="ev-ends" /></div>
            </div>
            <div><Label>{t.location}</Label><Input value={f.location} onChange={(e) => upd("location", e.target.value)} data-testid="ev-location" /></div>
            <div><Label>{t.description}</Label><Textarea rows={3} value={f.description} onChange={(e) => upd("description", e.target.value)} data-testid="ev-desc" /></div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-5" data-testid="wizard-step-2">
            <h2 className="font-serif text-3xl">Yayın ve Erişim</h2>
            <div><Label>{t.stream_url}</Label><Input value={f.stream_url} onChange={(e) => upd("stream_url", e.target.value)} placeholder="https://youtube.com/embed/..." data-testid="ev-stream" /><p className="text-xs text-muted-foreground mt-2">Demo: boş bırakabilirsiniz.</p></div>
            <div><Label>{t.event_password}</Label><Input type="password" value={f.password} onChange={(e) => upd("password", e.target.value)} minLength={4} data-testid="ev-password" /></div>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-5" data-testid="wizard-step-3">
            <h2 className="font-serif text-3xl">Hediye Ayarları</h2>
            <div className="flex items-center justify-between p-4 rounded-lg border">
              <div><Label>{t.accept_cash}</Label><p className="text-xs text-muted-foreground">Nakit hediye modülünü aç</p></div>
              <Switch checked={f.accept_cash} onCheckedChange={(v) => upd("accept_cash", v)} data-testid="ev-cash-toggle" />
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg border">
              <div><Label>{t.accept_gold}</Label><p className="text-xs text-muted-foreground">Altın hediye ve 90 sn kilit</p></div>
              <Switch checked={f.accept_gold} onCheckedChange={(v) => upd("accept_gold", v)} data-testid="ev-gold-toggle" />
            </div>
          </div>
        )}
        {step === 4 && (
          <div className="space-y-5" data-testid="wizard-step-4">
            <h2 className="font-serif text-3xl flex items-center gap-2"><Gem className="w-6 h-6 text-primary" /> {t.choose_jeweler}</h2>
            <p className="text-xs text-muted-foreground p-3 rounded-lg bg-muted/40">{t.marketplace_note}</p>
            <div>
              <Label>{t.city}</Label>
              <Select value={cityFilter || "all"} onValueChange={(v) => setCityFilter(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="ev-city"><SelectValue placeholder="Şehir filtrele" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tümü</SelectItem>
                  {["İstanbul", "Ankara", "İzmir", "Bursa", "Antalya"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              <button type="button" onClick={() => upd("jeweler_id", "")} data-testid="ev-jeweler-none"
                className={`w-full text-start p-4 rounded-lg border ${!f.jeweler_id ? "ring-1 ring-primary/50 bg-primary/5" : ""}`}>
                <div className="font-serif">Kuyumcu seçme</div>
                <div className="text-xs text-muted-foreground">{t.no_jeweler_hint}</div>
              </button>
              {jewelers.map((j) => (
                <button key={j.id} type="button" onClick={() => upd("jeweler_id", j.id)} data-testid={`ev-jeweler-${j.id}`}
                  className={`w-full text-start p-4 rounded-lg border transition-colors ${f.jeweler_id === j.id ? "ring-1 ring-primary/60 bg-primary/10" : ""}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-serif">{j.name}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" /> {j.city} · Komisyon {(j.commission_bps || 0) / 100}%</div>
                    </div>
                    {f.jeweler_id === j.id && <span className="text-primary text-sm">✓</span>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="flex justify-between mt-8">
          <Button variant="ghost" disabled={step === 1} onClick={() => setStep(step - 1)} data-testid="wizard-back">{t.back}</Button>
          {step < total ? (
            <Button className="rounded-full" onClick={() => setStep(step + 1)} data-testid="wizard-next">{t.continue}</Button>
          ) : (
            <Button className="rounded-full" onClick={submit} disabled={saving} data-testid="wizard-submit">{saving ? "..." : t.save}</Button>
          )}
        </div>
      </Card>
    </div>
  );
}
